import { Button } from "@/components/atoms";
import { Typography } from "@/components/atoms/Typography";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import React from "react";
import { Modal, StyleSheet, View, Platform } from "react-native";

export interface OkDialogProps {
  visible: boolean;
  title?: string;
  message: string;
  okText?: string;
  onOk: () => void;
  // Allow future optional props without breaking consumers
  [key: string]: any;
}

export const OkDialog: React.FC<Readonly<OkDialogProps>> = ({
  visible,
  title,
  message,
  okText,
  onOk,
}) => {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();

  const styles = StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: UI_PRIMITIVES.overlayBackdrop,
      justifyContent: "center",
      alignItems: "center",
      paddingHorizontal: 24,
    },
    container: {
      backgroundColor: colors.white,
      borderRadius: 8,
      paddingTop: 32,
      paddingBottom: 16,
      paddingHorizontal: 20,
      width: "100%",
      maxWidth: Platform.OS === 'web' ? 400 : "100%",
      alignItems: "center",
      gap: 24,
    },
    title: {
      textAlign: isRTL ? "right" : "center",
      width: "100%",
    },
    message: {
      textAlign: "center",
      width: "100%",
    },
    button: {
      width: "100%",
      height: 48,
    },
    buttonText: {
      fontSize: 16,
      fontWeight: "600",
      fontFamily: "Matter",
      lineHeight: 16,
    },
  });

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onOk}
    >
      <View style={styles.overlay}>
        <View style={styles.container}>
          {!!title && (
            <Typography variant="title3" color="primary" style={styles.title}>
              {title}
            </Typography>
          )}
          <Typography variant="body" color="primary" style={styles.message}>
            {message}
          </Typography>
          <Button
            title={okText || t("common.ok")}
            variant="primary"
            onPress={onOk}
            style={styles.button}
            textStyle={styles.buttonText}
          />
        </View>
      </View>
    </Modal>
  );
};

export default OkDialog;
